import { Link } from 'react-router-dom';
import { Button, IconButton } from '@c2fo/liquidity';
import { TimesIcon } from '@c2fo/liquidity/icons';
import reviewingoffers from '@/enterprise/assets/reviewingoffers.svg';
import useDismissDismissible from '@/enterprise/data/useDismissDismissible';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { useLendingModalContext } from './modal/LendingModalContext';
import { useShowLendingInlineAd } from './utils';

const LendingInlineAdComponent = ({ mode }: { mode: 'homepage' | 'buyers' }) => {
  const { openModal } = useLendingModalContext();
  const { mutateAsync: dismissDismissible } = useDismissDismissible();
  const { trackEnterpriseEvent } = useReporting();
  const { data: takerMarkets } = useTakerMarkets();
  const showLendingInlineAd = useShowLendingInlineAd(mode);

  if (!showLendingInlineAd) {
    return null;
  }

  const hasAvailableAP = takerMarkets?.some((tm) => tm.totalInvoiceAmount > 0);

  const handleOpenModal = () => {
    trackEnterpriseEvent('lending::modal::opened', { referrer: 'inline-ad', hasAvailableAP });
    openModal({ referrer: 'inline-ad' });
  };

  return (
    <div className="relative my-4 rounded-2xl bg-lightBlue-100 shadow">
      <div className="absolute right-2 top-2">
        <IconButton
          icon={TimesIcon}
          variant="ancillary"
          name="Close Lending banner"
          onClick={() => {
            dismissDismissible('LENDING_INLINE_AD');
            trackEnterpriseEvent('lending::inline-ad::dismissed', { hasAvailableAP });
          }}
        />
      </div>
      <div className="flex flex-col items-end justify-between px-8 py-4 md:flex-row md:py-2">
        <div className="flex flex-col items-center sm:flex-row md:items-stretch">
          <img src={reviewingoffers} alt="Reviewing offer graphic" className="max-w-40 md:max-w-52" />
          <div className="flex flex-col items-stretch justify-between md:py-4">
            <div>
              <p className="text-2xl font-bold">Looking for more working capital?</p>
              <p className="text-lg">
                With C2FO Lending Connections we can assist your company in accessing additional financing.
              </p>
            </div>
            <div className="pt-2 text-lg text-text-secondary">
              Learn more in our{' '}
              <Link
                className="font-semibold text-blue-500"
                to={asEnterpriseLink('lending')}
                onClick={() => {
                  trackEnterpriseEvent('lending::inline-ad::faqs::clicked');
                  setTimeout(() => {
                    const faqElement = document.getElementById('lending-faqs');
                    if (faqElement) {
                      faqElement.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 200);
                }}
              >
                FAQs
              </Link>{' '}
              or schedule a consultation with one of our Lending Connections Representatives.
            </div>
          </div>
        </div>
        <div className="w-full py-4 md:w-fit">
          <Button className="w-full text-nowrap md:w-fit" variant="primary" onClick={handleOpenModal}>
            Schedule Call
          </Button>
        </div>
      </div>
    </div>
  );
};

const LendingInlineAd = ({ mode }: { mode: 'homepage' | 'buyers' }) => {
  return (
    <QueryBoundaries>
      <LendingInlineAdComponent mode={mode} />
    </QueryBoundaries>
  );
};

export default LendingInlineAd;
